import { IslandApiResponse } from 'state/data/slice'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

import {
  AprCalculationParams,
  AprDisplayConfig,
  AprDisplayData,
  AprDisplayParams,
  DepositOption,
  ExternalLink,
  ExternalLinksParams,
  PoolConfig,
  PoolData,
  PoolMetrics,
  StakingData,
  StakingDataParams,
} from '../types'

export class V2PoolConfig implements PoolConfig {
  readonly type = 'V2' as const
  readonly provider = 'v2' as const
  readonly supportsZapping = false
  readonly supportsAutoCompound = false
  readonly supportsFarms = true
  readonly supportsVaults = true

  getPoolData(poolApiResponse: IslandApiResponse): PoolData {
    return {
      lowerTick: 0, // V2 doesn't use ticks
      upperTick: 0,
      currentTick: 0,
      feeTier: poolApiResponse?.feeTier ?? 3000,
      poolAddress: poolApiResponse?.poolAddress ?? '',
      lpTokenAddress: poolApiResponse?.tokenLp?.id ?? '',
    }
  }

  getStakingData(params: StakingDataParams): StakingData {
    const { poolApiResponse, kodiakFarmAddress, rewardVaultAddress, isWhitelistedForRewardVault } = params

    const isValidKodiakFarm = !!kodiakFarmAddress

    let stakingType: StakingData['stakingType'] = 'none'
    if (isWhitelistedForRewardVault && rewardVaultAddress) {
      stakingType = 'rewardVault'
    } else if (isValidKodiakFarm) {
      stakingType = 'kodiakFarm'
    }

    return {
      kodiakFarmAddress: isValidKodiakFarm ? kodiakFarmAddress : undefined,
      rewardVaultAddress: isWhitelistedForRewardVault ? rewardVaultAddress : undefined,
      baultAddress: poolApiResponse?.baults?.[poolApiResponse.baults.length - 1]?.id,
      stakingType,
      baultFarmAddress: '',
    }
  }

  getPoolMetrics(poolApiResponse: IslandApiResponse, pool?: any): PoolMetrics {
    return {
      tvl: poolApiResponse?.tvl ?? pool?.totalValueLockedUSD ?? 0,
      farmTvl: poolApiResponse?.farmTvl,
      baseApr: poolApiResponse?.apr ?? 0,
      farmApr: !poolApiResponse?.isRewardVault ? poolApiResponse?.farm?.apr : undefined,
      rewardVaultRewardApr: poolApiResponse?.isRewardVault ? poolApiResponse?.farm?.apr : undefined,
      weeklyFees: poolApiResponse?.weeklyFeesEarnedUSD ?? 0,
    }
  }

  getExternalLinks({
    chainId,
    poolApiResponse,
    kodiakFarmAddress,
    rewardVaultAddress,
  }: ExternalLinksParams): ExternalLink[] {
    const links: ExternalLink[] = []

    if (poolApiResponse?.id) {
      links.push({
        label: 'V2 Pool Contract',
        url: getExplorerLink(chainId, poolApiResponse.id, ExplorerDataType.ADDRESS),
        copyText: poolApiResponse.id,
      })
    }

    if (rewardVaultAddress) {
      links.push(
        {
          label: 'Reward Vault Contract',
          url: getExplorerLink(chainId, rewardVaultAddress, ExplorerDataType.ADDRESS),
          copyText: rewardVaultAddress,
        },
        {
          label: 'Berachain Hub',
          url: `https://hub.berachain.com/vaults/${rewardVaultAddress}`,
        }
      )
    }

    if (kodiakFarmAddress) {
      links.push({
        label: 'Farm Contract',
        url: getExplorerLink(chainId, kodiakFarmAddress, ExplorerDataType.ADDRESS),
        copyText: kodiakFarmAddress,
      })
    }

    return links
  }

  getAprDisplay({
    isRewardVaultActive,
    isKodiakFarmActive,
    rewardVaultRewardSymbol,
  }: AprDisplayParams): AprDisplayConfig {
    return {
      showBaseApr: true,
      showKodiakFarmApr: isKodiakFarmActive && !isRewardVaultActive,
      showRewardVaultRewardApr: isRewardVaultActive,
      farmLabel: isRewardVaultActive ? `${rewardVaultRewardSymbol || 'Reward'} APR` : 'Farm APR',
    }
  }

  getDepositOptions(): DepositOption[] {
    return [
      { value: 'depositOnly', label: 'Deposit Only', disabled: false },
      { value: 'withStaking', label: 'With Staking', disabled: false },
    ]
  }

  getDisplayApr({ farmApr, isLoadingFarm, metrics, aprDisplay }: AprCalculationParams): AprDisplayData | null {
    if (!aprDisplay.showKodiakFarmApr && !aprDisplay.showRewardVaultRewardApr) {
      return null
    }

    if (aprDisplay.showKodiakFarmApr && farmApr !== null) {
      return {
        apr: farmApr,
        label: aprDisplay.farmLabel,
        isLoading: isLoadingFarm,
      }
    }

    if (aprDisplay.showRewardVaultRewardApr && metrics.rewardVaultRewardApr !== undefined) {
      return {
        apr: metrics.rewardVaultRewardApr,
        label: aprDisplay.farmLabel,
        isLoading: false,
      }
    }

    return null
  }
}
